<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useCompanyLabelsStore } from 'dashboard/stores/companyLabels';
import { PRESET_LABEL_COLORS } from 'dashboard/helper/labelColor';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import AddLabel from 'dashboard/routes/dashboard/settings/labels/AddLabel.vue';

const { t } = useI18n();
const labelsStore = useCompanyLabelsStore();

const dialogRef = ref(null);
const addLabelDialogRef = ref(null);
const searchQuery = ref('');
const editingColor = ref({});
const editingTitle = ref({});
const colorPickerOpen = ref(null);

const PRESET_COLORS = PRESET_LABEL_COLORS;

const labels = computed(() => labelsStore.getLabels);

const filteredLabels = computed(() => {
  const q = searchQuery.value.trim().toLowerCase();
  if (!q) return labels.value;
  return labels.value.filter(l => l.title.toLowerCase().includes(q));
});

const canCreate = computed(() => {
  const q = searchQuery.value.trim().toLowerCase();
  if (!q) return false;
  return !labels.value.some(l => l.title === q);
});

const currentColor = computed(
  () => id =>
    editingColor.value[id] ||
    labels.value.find(l => l.id === id)?.color ||
    '#6B7280'
);

const currentTitle = id =>
  editingTitle.value[id] ?? (labels.value.find(l => l.id === id)?.title || '');

const handleTitleInput = (id, value) => {
  editingTitle.value = { ...editingTitle.value, [id]: value };
};

const handleTitleBlur = async label => {
  const newTitle = (editingTitle.value[label.id] ?? label.title).trim();
  if (!newTitle || newTitle === label.title) return;
  await labelsStore.update(label.id, {
    title: newTitle,
    color: currentColor.value(label.id),
  });
  const updated = { ...editingTitle.value };
  delete updated[label.id];
  editingTitle.value = updated;
};

const handleColorChange = async (label, color) => {
  editingColor.value = { ...editingColor.value, [label.id]: color };
  await labelsStore.update(label.id, {
    title: currentTitle(label.id) || label.title,
    color,
  });
};

const selectPresetColor = async (label, color) => {
  colorPickerOpen.value = null;
  await handleColorChange(label, color);
};

const handleDelete = async id => {
  await labelsStore.delete(id);
};

const handleCreate = () => {
  if (!canCreate.value) return;
  addLabelDialogRef.value?.open();
};

const handleAddLabelClose = () => {
  addLabelDialogRef.value?.close();
  searchQuery.value = '';
};

const handleClose = () => {
  searchQuery.value = '';
  editingColor.value = {};
  editingTitle.value = {};
  colorPickerOpen.value = null;
};

onMounted(() => labelsStore.fetchAll());

defineExpose({ dialogRef });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="t('COMPANIES_LAYOUT.LABELS.MANAGE_LABELS')"
    :show-cancel-button="false"
    :show-confirm-button="false"
    @close="handleClose"
  >
    <!-- Search / Create -->
    <div class="mb-3 flex gap-2">
      <input
        v-model="searchQuery"
        type="text"
        :placeholder="t('COMPANIES_LAYOUT.LABELS.SEARCH_LABELS')"
        class="reset-base flex-1 h-8 px-3 text-sm rounded-lg border border-n-weak bg-n-alpha-black2 dark:bg-n-solid-1 text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-n-blue-8"
        @keydown.enter.prevent.stop="handleCreate"
      />
      <button
        v-if="canCreate"
        type="button"
        class="shrink-0 h-8 px-3 text-sm rounded-lg bg-n-blue-9 text-white hover:bg-n-blue-10 transition-colors"
        @click="handleCreate"
      >
        {{
          t('BULK_ACTION.LABELS.CREATE_LABEL', { title: searchQuery.trim() })
        }}
      </button>
    </div>

    <div class="flex flex-col gap-0.5 min-h-[120px]">
      <div
        v-for="label in filteredLabels"
        :key="label.id"
        class="flex flex-col rounded-lg hover:bg-n-alpha-1 group"
      >
        <div class="flex items-center gap-2 px-2 py-1.5">
          <!-- Color swatch -->
          <button
            type="button"
            class="shrink-0 rounded-md h-4 w-4 border border-solid border-n-weak ring-offset-1 group-hover:ring-1 ring-n-blue-8 transition-shadow"
            :style="{ backgroundColor: currentColor(label.id) }"
            @click.stop="
              colorPickerOpen = colorPickerOpen === label.id ? null : label.id
            "
          />
          <!-- Editable title -->
          <input
            :value="currentTitle(label.id)"
            class="reset-base flex-1 text-sm text-n-slate-12 bg-transparent focus:outline-none focus:border-b focus:border-n-blue-8 truncate min-w-0"
            @input="e => handleTitleInput(label.id, e.target.value)"
            @blur="handleTitleBlur(label)"
            @keydown.enter.prevent="e => e.target.blur()"
          />
          <!-- Delete -->
          <button
            type="button"
            class="opacity-0 group-hover:opacity-100 p-0.5 rounded text-n-ruby-9 hover:bg-n-ruby-3 transition-all shrink-0"
            @click="handleDelete(label.id)"
          >
            <Icon icon="i-lucide-trash-2" class="size-3.5" />
          </button>
        </div>

        <!-- Color picker -->
        <div
          v-if="colorPickerOpen === label.id"
          class="px-2 pb-2 flex flex-col gap-2"
          @click.stop
        >
          <div class="flex flex-wrap gap-1.5">
            <button
              v-for="preset in PRESET_COLORS"
              :key="preset"
              type="button"
              class="h-5 w-5 rounded-md border border-solid border-n-weak hover:scale-110 transition-transform"
              :style="{ backgroundColor: preset }"
              :class="{
                'ring-2 ring-offset-1 ring-n-blue-8':
                  currentColor(label.id) === preset,
              }"
              @click="selectPresetColor(label, preset)"
            />
          </div>
          <label class="flex items-center gap-2 cursor-pointer">
            <span class="text-xs text-n-slate-11">{{
              t('BULK_ACTION.LABELS.CUSTOM_COLOR')
            }}</span>
            <input
              type="color"
              class="h-5 w-8 rounded cursor-pointer border border-n-weak bg-transparent"
              :value="currentColor(label.id)"
              @input="e => handleColorChange(label, e.target.value)"
            />
          </label>
        </div>
      </div>

      <div
        v-if="!filteredLabels.length"
        class="text-sm text-n-slate-11 px-2 py-3 text-center"
      >
        {{ t('DROPDOWN_MENU.EMPTY_STATE') }}
      </div>
    </div>
    <template #footer>
      <Button
        :label="t('BULK_ACTION.LABELS.DONE')"
        color="blue"
        class="w-full"
        @click="dialogRef.close()"
      />
    </template>
  </Dialog>

  <Dialog
    ref="addLabelDialogRef"
    :show-cancel-button="false"
    :show-confirm-button="false"
  >
    <AddLabel
      entity-key="company"
      :prefill-title="searchQuery"
      @close="handleAddLabelClose"
    />
  </Dialog>
</template>
